import { z } from 'zod';

import { DateStringSchema, LocalDateTimeStringSchema } from '../../common';
import { makeEndpoint } from '../common';

export enum PriceTierType {
  DEFAULT = 'default',
  SESSION = 'session',
  RULE = 'rule',
}

export enum PriceTierConditionType {
  DAY_OF_WEEK = 'day_of_week',
  DATE_RANGE = 'date_range',
  MATCH_DATE = 'match_date',
  MATCH_DURATION = 'match_duration',
  DURATION_RANGE = 'duration_range',
}

export const DateRangePriceTierConditionSchema = z.object({
  type: z.literal(PriceTierConditionType.DATE_RANGE),
  param: z
    .array(
      z.object({ from: DateStringSchema, to: DateStringSchema }).refine(({ from, to }) => from <= to, {
        message: 'The "from" date must be less than or equal to the "to" date.',
      }),
    )
    .min(1),
});
export type DateRangePriceTierCondition = z.infer<typeof DateRangePriceTierConditionSchema>;
export const DurationRangePriceTierConditionSchema = z.object({
  type: z.literal(PriceTierConditionType.DURATION_RANGE),
  param: z.array(z.object({ from: z.number(), to: z.number() })).min(1),
});
export type DurationRangePriceTierCondition = z.infer<typeof DurationRangePriceTierConditionSchema>;
export const MatchDatePriceTierConditionSchema = z.object({
  type: z.literal(PriceTierConditionType.MATCH_DATE),
  param: z.array(LocalDateTimeStringSchema).min(1),
});
export const DayOfWeekPriceTierConditionSchema = z.object({
  type: z.literal(PriceTierConditionType.DAY_OF_WEEK),
  param: z
    .array(z.number().min(1 /* MONDAY */).max(7 /* SUNDAY */))
    .min(1)
    .refine((days) => days.every((day) => day >= 1 && day <= 7), {
      message: 'Days of the week must be between 1 (Monday) and 7 (Sunday).',
    }),
});

export type DayOfWeekPriceTierCondition = z.infer<typeof DayOfWeekPriceTierConditionSchema>;
export const MatchDurationPriceTierConditionSchema = z.object({
  type: z.literal(PriceTierConditionType.MATCH_DURATION),
  param: z.array(z.number().min(1)).min(1, {
    message: 'Required',
  }),
});
export type MatchDurationPriceTierCondition = z.infer<typeof MatchDurationPriceTierConditionSchema>;

export const PriceTierConditionSchema = z.discriminatedUnion('type', [
  DateRangePriceTierConditionSchema,
  DurationRangePriceTierConditionSchema,
  MatchDatePriceTierConditionSchema,
  DayOfWeekPriceTierConditionSchema,
  MatchDurationPriceTierConditionSchema,
]);
export type PriceTierCondition = z.infer<typeof PriceTierConditionSchema>;

export const PriceTierSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  price: z.number(),
  maxQuantity: z.number().min(0).optional(),
  durationPrices: z.record(z.string(), z.number()).optional(),
});

export type PriceTier = z.infer<typeof PriceTierSchema>;

export const PostSessionPriceTierRuleRequestSchema = z.object({
  id: z.string().uuid(),
  startDateTime: LocalDateTimeStringSchema,
  sessionDuration: z.number().min(1).optional(),
  priceTiers: z.array(PriceTierSchema).min(1),
});

export const PostSessionPriceTierRuleEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, experienceId }: { brandId: string; experienceId: string }) =>
    `/v1/brands/${brandId}/experiences/${experienceId}/price-tier-rule/session`,
  requestSchema: PostSessionPriceTierRuleRequestSchema,
  responseSchema: z.void(),
});

export const PriceTierRulesSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nonempty(),
  order: z.number(),
  type: z.literal(PriceTierType.RULE),
  isActive: z.boolean(),
  conditions: z.array(PriceTierConditionSchema).min(1),
  priceTiers: z.array(PriceTierSchema).min(1),
});

export const PostPriceTierRuleRequestSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nonempty(),
  order: z.number(),
  isActive: z.boolean(),
  conditions: z.array(PriceTierConditionSchema).min(1),
  priceTiers: z.array(PriceTierSchema).min(1),
});

export const PostPriceTrierRuleEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, experienceId }: { brandId: string; experienceId: string }) =>
    `/v1/brands/${brandId}/experiences/${experienceId}/price-tier-rule`,
  requestSchema: PostPriceTierRuleRequestSchema,
  responseSchema: z.void(),
});

// Add a new schema for session data
const SessionDataSchema = z.object({
  startDateTime: LocalDateTimeStringSchema,
  sessionDuration: z.number().min(1).optional(),
});

export const PostResourceGroupSessionPriceTierRuleRequestSchema = z.object({
  sessions: z.array(SessionDataSchema).min(1),
  priceTiers: z.array(PriceTierSchema).min(1),
});

export const PostResourceGroupSessionPriceTierRuleEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, resourceGroupId }: { brandId: string; resourceGroupId: string }) =>
    `/v1/brands/${brandId}/experiences/resource-groups/${resourceGroupId}/price-tier-rule/resource-group-session`,
  requestSchema: PostResourceGroupSessionPriceTierRuleRequestSchema,
  responseSchema: z.void(),
});
